package com.rajen.angrejidictionary.pages.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.rajen.angrejidictionary.components.layouts.PageLayoutData
import com.rajen.angrejidictionary.data.local.AdminAuthManager
import com.rajen.angrejidictionary.presentation.viewmodel.AdminLoginViewModel
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

val AdminCardStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(Color.rgb(0xFF, 0xF7, 0xF1))
        .borderRadius(28.px)
        .padding(2.5.cssRem)
        .boxShadow(
            offsetY = 10.px,
            blurRadius = 30.px,
            color = Color.rgba(0, 0, 0, 0.06f)
        )
}

val AdminErrorStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(Color.rgb(0xFF, 0xEB, 0xEE))
        .borderRadius(8.px)
        .padding(0.75.cssRem)
        .color(Color.rgb(0xC6, 0x28, 0x28))
        .fontSize(0.9.cssRem)
}

val AdminButtonStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .padding(topBottom = 0.9.cssRem)
        .backgroundColor(Color.rgb(0xD3, 0x35, 0x3E))
        .color(Colors.White)
        .borderRadius(12.px)
        .fontWeight(FontWeight.Bold)
        .fontSize(1.cssRem)
        .cursor(Cursor.Pointer)
        .border(0.px, LineStyle.None, Colors.Transparent)
        .textAlign(TextAlign.Center)
}

@InitRoute
fun initAdminLogin(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        title = "Admin Login",
        description = "Secure admin access for Angreji Dictionary word management."
    ))
}

@Page("/admin/login")
@Layout(".components.layouts.PageLayout")
@Composable
fun AdminLoginPage() {
    val viewModel = remember { AdminLoginViewModel() }
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        if (AdminAuthManager.isLoggedIn()) {
            ctx.router.navigateTo("/admin")
        }
    }

    LaunchedEffect(viewModel.loginSuccess) {
        if (viewModel.loginSuccess) {
            ctx.router.navigateTo("/admin")
        }
    }

    Box(
        Modifier.fillMaxWidth(),
        contentAlignment = Alignment.Center
    ) {
        Column(
            Modifier
                .fillMaxWidth()
                .maxWidth(480.px)
                .padding(leftRight = 1.cssRem),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(
                text = "ADMIN LOGIN",
                modifier = Modifier
                    .fontSize(clamp(1.8.cssRem, 5.vw, 2.5.cssRem))
                    .fontWeight(FontWeight.Bold)
                    .color(Color.rgb(0xD3, 0x35, 0x3E))
                    .textAlign(TextAlign.Center)
            )

            SpanText(
                text = "Secure access to word management",
                modifier = Modifier
                    .margin(top = 0.5.cssRem)
                    .fontSize(1.cssRem)
                    .color(Color.rgb(0xB6, 0x9B, 0x8C))
                    .textAlign(TextAlign.Center)
            )

            Column(
                AdminCardStyle.toModifier().margin(top = 2.cssRem),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(1.25.cssRem)
            ) {
                viewModel.errorMessage?.let { error ->
                    Div(AdminErrorStyle.toAttrs()) { Text(error) }
                }

                Column(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
                    SpanText(
                        "Username",
                        Modifier
                            .fontSize(0.9.cssRem)
                            .fontWeight(FontWeight.Medium)
                            .color(Color.rgb(0x21, 0x00, 0x00))
                    )
                    org.jetbrains.compose.web.dom.TextInput(
                        value = viewModel.username,
                        attrs = {
                            onInput { viewModel.onUsernameChange(it.value) }
                            onKeyDown { event ->
                                if (event.key == "Enter") {
                                    scope.launch { viewModel.login() }
                                }
                            }
                            style {
                                width(100.percent)
                                padding(0.85.cssRem)
                                borderRadius(12.px)
                                border { width(1.px); style(LineStyle.Solid); color(Color.rgb(0xDD, 0xCC, 0xBB)) }
                                fontSize(1.cssRem)
                                color(Color.rgb(0x21, 0x00, 0x00))
                                backgroundColor(Color.rgb(0xFF, 0xFF, 0xFF))
                                property("box-sizing", "border-box")
                                property("outline", "none")
                            }
                        }
                    )
                }

                Column(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
                    SpanText(
                        "Password",
                        Modifier
                            .fontSize(0.9.cssRem)
                            .fontWeight(FontWeight.Medium)
                            .color(Color.rgb(0x21, 0x00, 0x00))
                    )
                    org.jetbrains.compose.web.dom.Input(type = org.jetbrains.compose.web.attributes.InputType.Password) {
                        value(viewModel.password)
                        onInput { viewModel.onPasswordChange(it.value) }
                        onKeyDown { event ->
                            if (event.key == "Enter") {
                                scope.launch { viewModel.login() }
                            }
                        }
                        style {
                            width(100.percent)
                            padding(0.85.cssRem)
                            borderRadius(12.px)
                            border { width(1.px); style(LineStyle.Solid); color(Color.rgb(0xDD, 0xCC, 0xBB)) }
                            fontSize(1.cssRem)
                            color(Color.rgb(0x21, 0x00, 0x00))
                            backgroundColor(Color.rgb(0xFF, 0xFF, 0xFF))
                            property("box-sizing", "border-box")
                            property("outline", "none")
                        }
                    }
                }

                Div(
                    AdminButtonStyle.toAttrs {
                        onClick { scope.launch { viewModel.login() } }
                    }
                ) {
                    Text(if (viewModel.isLoading) "Signing in..." else "Sign In")
                }
            }
        }
    }
}